import {
  AccountResponse,
  FoundVillageResponse,
  GuestAuthResponse,
  type OwnedVillage,
  LoginResponse,
  RefreshResponse,
} from "@empire/shared";
import { ApiProblem, Unreachable, request } from "./client.js";

/**
 * The guest session, and the one piece of state the app keeps between visits.
 *
 * The refresh token IS the account (S5-1): there is no email and no password to
 * fall back on, so losing this string loses the village. That single fact
 * decides everything in this file — it is why a failed refresh does NOT quietly
 * sign up again (see `refresh`), and why the token is written to storage before
 * the response is considered handled.
 */

const KEY = "empire.session.v1";
const DEVICE_KEY = "empire.device.v1";

/** Refresh this long before the access token actually expires. */
const REFRESH_MARGIN_MS = 60_000;

export interface Session {
  readonly accessToken: string;
  readonly refreshToken: string;
  /** Wall-clock ms when the ACCESS token stops being accepted. */
  readonly accessExpiresAt: number;
  readonly accountId: string;
  readonly playerId: string;
  /** "" until the account has founded or chosen a village (D-117). */
  readonly villageId: string;
}

/**
 * Storage that cannot throw.
 *
 * `localStorage` is unavailable in a Node test, and throws rather than returns
 * null in a Safari private window and behind some corporate policies. A game
 * that crashes on startup because it could not read a key it has never written
 * is a worse outcome than a game that forgets who you are.
 */
const store = {
  get(key: string): string | null {
    try {
      return globalThis.localStorage?.getItem(key) ?? null;
    } catch {
      return null;
    }
  },
  set(key: string, value: string): void {
    try {
      globalThis.localStorage?.setItem(key, value);
    } catch {
      /* ignore: the session simply will not survive a reload */
    }
  },
  remove(key: string): void {
    try {
      globalThis.localStorage?.removeItem(key);
    } catch {
      /* ignore */
    }
  },
};

export function loadSession(): Session | null {
  const raw = store.get(KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<Session>;
    if (!parsed.refreshToken || !parsed.accessToken) return null;
    return {
      accessToken: parsed.accessToken,
      refreshToken: parsed.refreshToken,
      accessExpiresAt: parsed.accessExpiresAt ?? 0,
      accountId: parsed.accountId ?? "",
      playerId: parsed.playerId ?? "",
      villageId: parsed.villageId ?? "",
    };
  } catch {
    return null;
  }
}

export function saveSession(session: Session): Session {
  store.set(KEY, JSON.stringify(session));
  return session;
}

export function clearSession(): void {
  store.remove(KEY);
}

/**
 * A stable-ish per-install string.
 *
 * Deliberately random rather than derived from the browser: a real fingerprint
 * is a tracking identifier, and the server only ever compares these for
 * equality when counting how many accounts came from one device. Generating it
 * once and storing it is honest about what it is.
 */
export function deviceFingerprint(): string {
  const existing = store.get(DEVICE_KEY);
  if (existing) return existing;
  const fresh =
    globalThis.crypto?.randomUUID?.() ??
    `dev-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
  store.set(DEVICE_KEY, fresh);
  return fresh;
}

function platform(): "web" | "android" | "ios" {
  // Capacitor sets this when the app is running inside the native shell.
  const cap = (globalThis as { Capacitor?: { getPlatform?: () => string } }).Capacitor;
  const name = cap?.getPlatform?.();
  return name === "android" || name === "ios" ? name : "web";
}

/** Create a brand-new guest account. Only ever called with the user's consent. */
export async function signUpAsGuest(): Promise<Session> {
  const body = await request("/api/v1/auth/guest", GuestAuthResponse, {
    method: "POST",
    body: { device: { platform: platform(), fingerprint: deviceFingerprint() } },
  });
  return saveSession({
    accessToken: body.accessToken,
    refreshToken: body.refreshToken,
    accessExpiresAt: Date.now() + body.expiresIn * 1000,
    accountId: body.accountId,
    playerId: body.playerId,
    villageId: body.villageId,
  });
}

/** Raised when the account can no longer be reached and only a new one will do. */
export class SessionLost extends Error {
  constructor() {
    super("this device is no longer signed in");
    this.name = "SessionLost";
  }
}

/**
 * Swap the refresh token for a new pair.
 *
 * A refusal here is NOT recoverable by signing up again, however tempting that
 * is: the server revokes a whole token chain the moment it sees a replay, so a
 * 401 can mean someone else is holding a copy of this account's token. Creating
 * a fresh guest account at that point would hand the player an empty village
 * and silently abandon the one with their maze in it, with no way back. So this
 * throws, the app says so, and starting over stays a decision the player makes.
 */
export async function refresh(session: Session): Promise<Session> {
  let body: RefreshResponse;
  try {
    body = await request("/api/v1/auth/refresh", RefreshResponse, {
      method: "POST",
      body: { refreshToken: session.refreshToken },
    });
  } catch (error) {
    if (error instanceof ApiProblem) throw new SessionLost();
    throw error; // Unreachable: the token is probably fine, the network is not.
  }
  return saveSession({
    ...session,
    accessToken: body.accessToken,
    refreshToken: body.refreshToken,
    accessExpiresAt: Date.now() + body.expiresIn * 1000,
  });
}

/**
 * A session whose access token is good for at least another minute.
 *
 * Concurrent callers share one refresh. Without that, opening the app fires the
 * layout load and the health check together, both find an expired token, both
 * refresh — and the second one presents a token the first has just rotated
 * away, which the server correctly reads as a replay and answers by revoking
 * everything. The app would log itself out on startup, occasionally, on a slow
 * connection. One promise is the whole fix.
 */
let inFlight: Promise<Session> | null = null;

export async function fresh(session: Session, now: number = Date.now()): Promise<Session> {
  if (session.accessExpiresAt - now > REFRESH_MARGIN_MS) return session;
  inFlight ??= refresh(session).finally(() => {
    inFlight = null;
  });
  return inFlight;
}

/** Test seam: forget any shared refresh that is mid-flight. */
export function resetRefreshState(): void {
  inFlight = null;
}

export { Unreachable };

/** Guest or saved (D-116), and the villages it owns (D-117). */
export async function fetchAccount(
  token: string
): Promise<{ username: string | null; villages: OwnedVillage[] }> {
  const body = await request("/api/v1/auth/me", AccountResponse, { token });
  return { username: body.username, villages: body.villages };
}

/** Create an account: a username and password, no village yet (D-117). */
export async function signUp(username: string, password: string): Promise<Session> {
  const body = await request("/api/v1/auth/signup", LoginResponse, {
    method: "POST",
    body: { username, password, device: { platform: platform(), fingerprint: deviceFingerprint() } },
  });
  return saveSession({
    accessToken: body.accessToken,
    refreshToken: body.refreshToken,
    accessExpiresAt: Date.now() + body.expiresIn * 1000,
    accountId: body.accountId,
    playerId: body.playerId ?? "",
    villageId: "",
  });
}

/** Found this account's village with the name the player chose (D-117). */
export async function foundVillage(token: string, name: string): Promise<OwnedVillage> {
  const body = await request("/api/v1/villages", FoundVillageResponse, {
    method: "POST",
    token,
    body: { name },
  });
  return { villageId: body.villageId, name: body.name, x: body.x, y: body.y };
}

/** Put a username and password on the account this session belongs to. */
export async function registerAccount(
  token: string,
  username: string,
  password: string
): Promise<string> {
  const body = await request("/api/v1/auth/register", AccountResponse, {
    method: "POST",
    token,
    body: { username, password },
  });
  return body.username ?? username;
}

/**
 * Open a saved village on this device. The new session REPLACES whatever this
 * device held — a guest village left behind here is still on the server, but
 * nothing on this device points at it any more.
 */
export async function logIn(username: string, password: string): Promise<Session> {
  const body = await request("/api/v1/auth/login", LoginResponse, {
    method: "POST",
    body: { username, password, device: { platform: platform(), fingerprint: deviceFingerprint() } },
  });
  return saveSession({
    accessToken: body.accessToken,
    refreshToken: body.refreshToken,
    accessExpiresAt: Date.now() + body.expiresIn * 1000,
    accountId: body.accountId,
    playerId: body.playerId ?? "",
    // Chosen on the next screen, not assumed here (D-117).
    villageId: "",
  });
}
